// Angular Files
import { CommonModule } from '@angular/common';
import {
    Component,
    EventEmitter,
    Input,
    NgModule,
    OnDestroy,
    OnInit,
    Output
} from '@angular/core';
import {
 FormControl, FormGroup, ReactiveFormsModule, Validators
} from '@angular/forms';

// Angular Material Files
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

// Other External Files
import { Subscription } from 'rxjs';

// Teller Online Files
// TODO (PROD-156): Remove ref to Enum Dto
import { ConfirmCodeRequestTypeEnumDto } from 'apps/public-portal/src/app/core/api/TellerOnlineIdentityApiClients';
import { AuthService, ResendEmailConfirmationRequestModel } from 'apps/public-portal/src/app/core/services';

// Teller Online Library Files
import { TellerOnlineAppService } from 'teller-online-libraries/core';
import { TellerOnlineSharedModule, TellerOnlineValidationService } from 'teller-online-libraries/shared';

// Component Files
import { CodeInputModule } from 'apps/public-portal/src/app/shared/components/code-input/code-input.component';

@Component({
    selector: 'app-code-verification',
    templateUrl: './code-verification.component.html',
    styleUrls: ['./code-verification.component.scss'],
    host: {
        class: 'code-verification'
    },
    standalone: false
})
export class CodeVerificationComponent implements OnInit, OnDestroy {
    // Declare @Input variables
    @Input() signUpToken: string;

    @Input() email: string;

    @Input() phone: string;

    @Input() authentication: string;

    @Input() codeSent: boolean;

    @Input() requestType: ConfirmCodeRequestTypeEnumDto;

    // True when the code is the whole verification, so this component confirms it itself as soon
    // as it's complete. False when it's just one field of the host's form, which submits instead.
    @Input() verifiesCode: boolean = false;

    // True when no host button submits this component, so it renders its own
    @Input() showSubmitButton: boolean = false;

    @Input() modal: boolean = false;

    @Input('aria-label') ariaLabel: string = 'Verification Code';

    // Declare @Output variables
    @Output() codeChange: EventEmitter<string> = new EventEmitter<string>();

    @Output() verified: EventEmitter<void> = new EventEmitter<void>();

    @Output() submitRequested: EventEmitter<void> = new EventEmitter<void>();

    @Output() resendCode: EventEmitter<boolean> = new EventEmitter<boolean>();

    // Public variables
    public codeControl = new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required, Validators.pattern(/^\d{6}$/)]
    });

    public form = new FormGroup({ 'Verification Code': this.codeControl });

    public resendCounter: number = 0;

    public ariaResendCounter: number = 0;

    public resendEnabled: boolean = true;

    public requestTypeText: string = 'email';

    // Private variables
    private _validatingCode: boolean = false;

    private _resendInterval: ReturnType<typeof setInterval>;

    private _valueSub: Subscription;

    constructor(
        private authService: AuthService,
        public appService: TellerOnlineAppService,
        public validationService: TellerOnlineValidationService
    ) {}

    ngOnInit(): void {
        if (this.requestType === ConfirmCodeRequestTypeEnumDto.Phone) {
            this.requestTypeText = 'phone';
            if (!this.codeSent) {
                this.authService.resendPhoneCode();
                this.codeSent = true;
            }
        } else {
            this.requestTypeText = 'email';
            if (!this.codeSent) {
                const request = new ResendEmailConfirmationRequestModel();
                request.email = this.email;
                request.requestType = this.requestType;
                this.authService.resendEmailCode(request);
                this.codeSent = true;
            }
        }

        this._disableResend(this.codeSent);

        this._valueSub = this.codeControl.valueChanges.subscribe(v => this.on_codeChange(v));
    }

    ngOnDestroy(): void {
        if (this._resendInterval) clearInterval(this._resendInterval);
        if (this._valueSub) this._valueSub.unsubscribe();
    }

    public on_codeChange(value: string): void {
        this.codeChange.emit(value);

        // Filling the last digit — by typing or pasting — is itself the submit gesture
        if (this.verifiesCode && this.codeControl.valid) {
            this._submit();
        }
    }

    public on_enter(): void {
        this._submit();
    }

    public onClick_submit(): void {
        this._validateVerificationCode();
    }

    public async onClick_resendCode(): Promise<void> {
        if (!this.resendEnabled) return;

        // Start the countdown before the request, so clicks while it is in flight send nothing
        this._disableResend();

        try {
            if (this.requestType === ConfirmCodeRequestTypeEnumDto.Phone) {
                await this.authService.resendPhoneCode();
            } else {
                const request = new ResendEmailConfirmationRequestModel();
                request.email = this.email;
                request.requestType = this.requestType;
                await this.authService.resendEmailCode(request);
            }
        } catch (e) {
            // No code went out, so the user may try again straight away
            this._enableResend();
            throw e;
        } finally {
            this.appService.finishPageLoading();
        }

        this.resendCode.emit(true);
    }

    public get ariaResendStatus(): string {
        if (this.resendCounter > 0) {
            // Update the screen-reader count on 10s boundaries so the countdown is announced
            // without exceeding ARIA live-region limits
            if (this.resendCounter % 10 === 0) {
                this.ariaResendCounter = this.resendCounter;
            }
            return `${this.ariaResendCounter} seconds.`;
        }
        return 'Try again';
    }

    private _submit(): void {
        if (this.verifiesCode) {
            this._validateVerificationCode();
        } else {
            this.submitRequested.emit();
        }
    }

    private async _validateVerificationCode(): Promise<void> {
        if (this._validatingCode) return;

        if (!this.validationService.runValidation(this.form)) return;

        this._validatingCode = true;
        this.appService.triggerPageLoading();

        let verificationError = false;
        try {
            await this.authService.confirmCode(
                this.codeControl.value,
                this.requestType,
                this.email,
                this.signUpToken,
                this.authentication
            );
            this.verified.emit();
        } catch (e) {
            verificationError = true;
            throw e;
        } finally {
            this._validatingCode = false;
            // Sign-up keeps the loader up while it redirects; everything else releases it
            if (verificationError || !this.appService.currentUrl.includes('sign-up')) {
                this.appService.finishPageLoading();
            }
        }
    }

    private _disableResend(codeSent: boolean = true): void {
        if (!codeSent) return;

        this.resendEnabled = false;
        this.resendCounter = 60;
        this.ariaResendCounter = this.resendCounter;

        this._resendInterval = setInterval(() => {
            this.resendCounter -= 1;
            if (this.resendCounter === 0) this._enableResend();
        }, 1000);
    }

    private _enableResend(): void {
        clearInterval(this._resendInterval);
        this.resendCounter = 0;
        this.resendEnabled = true;
    }
}

@NgModule({
    imports: [
        CommonModule,
        ReactiveFormsModule,
        TellerOnlineSharedModule,
        CodeInputModule,
        MatButtonModule,
        MatProgressSpinnerModule
    ],
    declarations: [CodeVerificationComponent],
    exports: [CodeVerificationComponent]
})
export class CodeVerificationModule { }
